import { createStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { FieldMetadataType, ReportType } from '../../../../../schemas/report.schemas';
import { getReportUniqueBusinessKey } from '../../../../../utils/common';
import { DIFF_TYPE, RECONCILIATION_OPERATION } from '../../constants/peerDiff';
import { Diff } from '../../utils/peerDiffUtil';
import { DIFF_DETECTOR_OPTION, DiffDetectorOptionType, SUPPORTED_DATA, SupportedDataType } from './constants';
import {
    getCoreValueDiff,
    getEffectiveScale,
    getHightestPriorityDiffType,
    GetPropDiffReturnType,
    getReportedAsLabelDiff,
    getSrcDiff,
    getSrcPriority,
    getWeakSimilarityEnabled,
} from './utils';
import Assert from '../../../../../utils/asserts';
import ReportUtils from '../../../../../utils/reportUtils';
import { MetadataField, SrcFieldValue } from '../../../../../constnats/reportConstants';
import _ from 'lodash';
import { ApplyableCheckType, DiffDetectorConfigType, FieldKnowledgeType, PeerFieldKnowledgeType } from './types';
import ObjCheck from '../../../../../utils/objCheck';
import type { ReconciliationEvent } from '../../../../../types/reconciliation.types';
export type { ReconciliationEvent } from '../../../../../types/reconciliation.types';

type ReportKnowledgeType = {
    branchName: string;
    descriptorMappings: Record<string, string>;
    branchSupportedData: SupportedDataType[];
    report: ReportType;
};

export type PeerBranchType = {
    schemaId: number;
    branchName: string;
    reports: ReportType[];
    branchSupportedData: SupportedDataType[];
    descriptorMappings: Record<string, string>;
};

export type PeerBranchesState = {
    peerBranches: PeerBranchType[];
    events: ReconciliationEvent[];
    currentBranchSupportedData: SupportedDataType[]
    currentSchemaId: number;

};

export type PeerBranchesActions = {
    actions: {
        getPeerReportDiffDetector: (reportBusinessKey: string, reportId:number, schemaName:string) => ({
            descriptorId,
            coreValue,
            fieldSpecificMetadata, }: {
                descriptorId: string;
                coreValue: string;
                fieldSpecificMetadata: FieldMetadataType;
                reportSrc: string;
            }) => Diff[];
        setEvents: (events: ReconciliationEvent[]) => void
        addReconciliationEvents: (events: ReconciliationEvent[]) => void
    };
};

export type PeersReportsStoreType = PeerBranchesState & PeerBranchesActions;

function createPeerBranchesStore(
    initialState: PeerBranchesState = {
        peerBranches: [],
        events: [],
        currentSchemaId: 0,
        currentBranchSupportedData: [],
    }
) {
    return createStore<PeersReportsStoreType>()(
        devtools(
            immer((set, get) => ({
                ...initialState,
                actions: {
                    getPeerReportDiffDetector: (reportBusinessKey, reportId, schemaName) => {
                        const weakSimilarityEnabled = getWeakSimilarityEnabled(schemaName);
                        const peerReportsKnowledge = get().peerBranches
                            .map(branch => {
                                const report = branch.reports.find(
                                    (report) => getReportUniqueBusinessKey(report) === reportBusinessKey)

                                return report === undefined ? null : {
                                    branchName: branch.branchName,
                                    descriptorMappings: branch.descriptorMappings,
                                    branchSupportedData: branch.branchSupportedData,
                                    report
                                }
                            })
                            .filter(r => r !== null);

                        return ({ descriptorId, coreValue, fieldSpecificMetadata, reportSrc }) => {

                            const rejectedEvents = get().events.filter(
                                (e) =>
                                    e.operation === RECONCILIATION_OPERATION.REJECT &&
                                    e.schemaId === get().currentSchemaId
                            );
                            return peerReportsKnowledge
                                .map((reportKnowledge) => {
                                    if (reportKnowledge.descriptorMappings[descriptorId]) {
                                       const diffConfig = getDiffDetectorConfig(
                                           reportKnowledge,
                                           get().currentBranchSupportedData,
                                           reportSrc
                                       );

                                        return getDiff(
                                            getCurrentFieldKnowledge(
                                                coreValue,
                                                fieldSpecificMetadata,
                                                reportSrc,
                                                weakSimilarityEnabled
                                            ),
                                            getPeerFieldKnowledge(
                                                reportKnowledge.descriptorMappings[descriptorId],
                                                reportKnowledge.report,
                                                reportKnowledge.branchName,
                                                weakSimilarityEnabled
                                            ),
                                            diffConfig,
                                            rejectedEvents,
                                            descriptorId,
                                            reportId
                                        );
                                    } else {
                                        return null;
                                    }
                                })
                                .filter((d) => d !== null);
                        };
                    },
                    setEvents: (events) => {
                        set(state => {
                            state.events = events
                        })
                    },
                    addReconciliationEvents: (events) => {
                        set(state => {
                            state.events.push(...events)
                        })
                    }
                },
            }))
        )
    );
}

const APPLYABLE_CHECK_BASED_ON_SRC: ApplyableCheckType = (currentField, peerField) =>
    getSrcPriority(currentField.effectiveSrc) >= getSrcPriority(peerField.effectiveSrc);
const APPLYABLE_CHECK_ALWAYS_FALSE: ApplyableCheckType = (_, __) => false;
const APPLYABLE_CHECK_ALWAYS_TRUE: ApplyableCheckType = (_, __) => true;

const isCd = (sData: SupportedDataType[]) => sData.includes(SUPPORTED_DATA.CD) && sData.length === 1

function getDiffDetectorConfig(
    peerReportKnowledge: ReportKnowledgeType,
    currentBranchSupportedData: SupportedDataType[],
    currentReportSrc: string
): DiffDetectorConfigType {
    const diffConfig: DiffDetectorConfigType = { globalOptions: [], applyableCheck: APPLYABLE_CHECK_ALWAYS_FALSE };


    if (isCd(currentBranchSupportedData)) {
        if (currentReportSrc === SrcFieldValue.MIXED) {
            diffConfig.applyableCheck = APPLYABLE_CHECK_ALWAYS_TRUE
        } else {
            diffConfig.applyableCheck = APPLYABLE_CHECK_BASED_ON_SRC
        }
        diffConfig.globalOptions.push(DIFF_DETECTOR_OPTION.IGNORE_REPORTED_AS_LABELS);
        diffConfig.globalOptions.push(DIFF_DETECTOR_OPTION.IGNORE_SRC);
    } else {
        if (isCd(peerReportKnowledge.branchSupportedData)) {
            if (peerReportKnowledge.report.src === SrcFieldValue.MIXED) {
                diffConfig.applyableCheck = APPLYABLE_CHECK_ALWAYS_FALSE
            } else {
                diffConfig.applyableCheck = APPLYABLE_CHECK_BASED_ON_SRC
            }
            diffConfig.globalOptions.push(DIFF_DETECTOR_OPTION.IGNORE_REPORTED_AS_LABELS);
            diffConfig.globalOptions.push(DIFF_DETECTOR_OPTION.IGNORE_SRC);
        } else {
            diffConfig.applyableCheck = APPLYABLE_CHECK_BASED_ON_SRC
        }
    }

    Assert.notNullOrUndefined(diffConfig.applyableCheck, "applyableCheck")

    return diffConfig;
}

function getPeerFieldKnowledge(
    descriptorId: string,
    peerReport: ReportType,
    branchName: string,
    weakSimilarityEnabled:boolean,
): PeerFieldKnowledgeType {
    const fieldMetadata = peerReport.metadata.fieldSpecific[descriptorId];

    const effectiveSrc = ReportUtils.getEffectiveSrc(peerReport.src, fieldMetadata?.[MetadataField.SRC] ?? '');
    const effectiveScale = getEffectiveScale(fieldMetadata?.[MetadataField.INPUT_SCALE] ?? 1, weakSimilarityEnabled);
    const coreValue = peerReport.dynamicFields[descriptorId] ?? '';
    const reportedAsLabel = fieldMetadata?.[MetadataField.REPORTED_AS_LABEL] ?? '';
    const manuallyCalculated = !!fieldMetadata?.[MetadataField.MANUALLY_CALCED]

    return { coreValue, reportedAsLabel, effectiveSrc, branchName, scale: effectiveScale, manuallyCalculated };
}

function getCurrentFieldKnowledge(
    coreValue: string,
    fieldMetadata: FieldMetadataType,
    reportSrc: string,
    weakSimilarityEnabled: boolean,
): FieldKnowledgeType {
    const effectiveSrc = ReportUtils.getEffectiveSrc(reportSrc, fieldMetadata[MetadataField.SRC] ?? '');
    const reportedAsLabel = fieldMetadata[MetadataField.REPORTED_AS_LABEL] ?? '';
    const effectiveScale = getEffectiveScale(fieldMetadata[MetadataField.INPUT_SCALE] ?? 1, weakSimilarityEnabled);
    const manuallyCalculated = !!fieldMetadata[MetadataField.MANUALLY_CALCED]
    return { coreValue, reportedAsLabel, effectiveSrc, scale: effectiveScale, manuallyCalculated };
}


function getDiff(currentField: FieldKnowledgeType, peerField: PeerFieldKnowledgeType,
    diffConfig: DiffDetectorConfigType, rejectedEvents: ReconciliationEvent[], descriptorId: string, reportId:number) {

    const diffs: GetPropDiffReturnType[] = [
        getReportedAsLabelDiff(currentField.reportedAsLabel, peerField.reportedAsLabel, diffConfig),
        getCoreValueDiff(currentField.coreValue, peerField.coreValue, currentField.scale, peerField.scale),
        getSrcDiff(currentField.effectiveSrc, peerField.effectiveSrc, diffConfig)
    ]

    // This and applyable must be calculated automatically inside the Diff
    const globalDiffType = getHightestPriorityDiffType(diffs.map(d => d.diffType));

    let diff = null;
    if (globalDiffType === DIFF_TYPE.MINOR) {
        const diffApplyable = diffConfig.applyableCheck(currentField, peerField)
        diff = new Diff({
            peerBranchName: peerField.branchName,
            diffs: diffs.map(d => d.propDiff).filter(d => d != null).map(d => {
                
                let applyable = diffApplyable && !rejectedEvents
                    .find(e =>
                        _.camelCase(e.property) === d.prop &&
                        e.currentValue === d.currentValue &&
                        e.targetValue === d.peerValue &&
                        e.descriptorId === descriptorId && 
                        e.reportId === reportId);

                if (d.prop === 'reportedAsLabel' &&
                    ObjCheck.isNullUndefinedOrEmpty(d.peerValue) &&
                    !currentField.manuallyCalculated &&
                    !ObjCheck.isNullUndefinedEmptyDashOrSpace(currentField.coreValue)) {
                    applyable = false;
                }

                d.applyable = applyable
                return d
            }),
            diffType: globalDiffType,
            applyable: diffApplyable,
        });
    } else {
        diff = new Diff({
            peerBranchName: peerField.branchName,
            diffs: diffs.map(d => d.propDiff).filter(d => d != null),
            diffType: globalDiffType
        });
    }

    Assert.notNullOrUndefined(diff, 'getPeerReportDiffDetector');
    return diff;
};

export default createPeerBranchesStore;
